import { HTMLAttributes, ReactNode } from "react";
import { cn } from "@/lib/utils";
import { useLayoutContext } from "@/contexts/LayoutContext";

export type PageContainerWidth = "narrow" | "default" | "wide" | "full";

const widthClassMap: Record<PageContainerWidth, string> = {
  narrow: "max-w-3xl",
  default: "max-w-5xl",
  wide: "max-w-7xl",
  full: "max-w-[1920px]",
};

export interface PageContainerProps extends HTMLAttributes<HTMLDivElement> {
  children: ReactNode;
  width?: PageContainerWidth;
  bleed?: boolean;
  disablePadding?: boolean;
  disableBottomPadding?: boolean;
}

/**
 * Shared responsive wrapper that enforces consistent gutters,
 * safe-area aware bottom padding, and max widths across pages.
 */
export function PageContainer({
  children,
  className,
  width = "wide",
  bleed = false,
  disablePadding = false,
  disableBottomPadding = false,
  ...rest
}: PageContainerProps) {
  const { insideLayout } = useLayoutContext();

  // When inside a layout (LayoutWrapper), skip horizontal padding since layout already provides it
  const shouldSkipHorizontalPadding = insideLayout || disablePadding;

  return (
    <div
      className={cn(
        "page-container w-full",
        !bleed && "mx-auto",
        !bleed && widthClassMap[width],
        !shouldSkipHorizontalPadding && "px-4 sm:px-6 lg:px-8",
        !disablePadding && !disableBottomPadding && !insideLayout && "py-4 sm:py-6",
        !disablePadding && disableBottomPadding && !insideLayout && "pt-4 sm:pt-6",
        !disableBottomPadding &&
          "pb-[calc(5rem+env(safe-area-inset-bottom,0px))] md:pb-10",
        className
      )}
      style={{ position: 'relative' }}
      {...rest}
    >
      {children}
    </div>
  );
}
